import React, { useEffect, useRef, useState } from 'react'
import { useMutation } from '@apollo/client'
import { CASE_ACTIVITIES, CREATE_COLLECTION_ACTIVITY } from './queries'

const HOT_BUTTONS = [
    { type: 'PROMISE_TO_PAY', label: 'Payment arrangement', icon: 'fa-handshake-o' },
    { type: 'CALL', label: 'Call', icon: 'fa-phone' },
    { type: 'CALL', label: 'Voicemail', icon: 'fa-volume-control-phone', defaultNote: 'LVM — no answer' },
    { type: 'SMS', label: 'Message', icon: 'fa-comment' },
]

// All activity types from billing.models.CollectionActivity.ActivityType.
// Anything already represented as a hot button is excluded from the dropdown.
const ALL_TYPES = [
    'NOTE',
    'CALL',
    'SMS',
    'EMAIL',
    'PROMISE_TO_PAY',
    'PAYMENT_PLAN_CREATED',
    'PAYMENT_SCHEDULED',
    'CHARGE_ATTEMPTED',
    'CHARGE_FAILED',
    'CHARGE_SUCCEEDED',
    'POLICY_APPLIED',
    'POLICY_REVERTED',
]
const HOT_BUTTON_TYPES = new Set(HOT_BUTTONS.map((b) => b.type))
const DROPDOWN_TYPES = ALL_TYPES.filter((t) => !HOT_BUTTON_TYPES.has(t))

function prettyType(t) {
    return t
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
}

export default function ActivityComposer({ caseId, focusOnMount, onFocused }) {
    // staged: { type: string, label: string, defaultNote?: string } | null
    const [staged, setStaged] = useState(null)
    const [noteText, setNoteText] = useState('')
    const textareaRef = useRef(null)

    // Pencil-from-row entry point: focus the textarea immediately, and stage NOTE
    // so the rep can just type and Send.
    useEffect(() => {
        if (focusOnMount && !staged) {
            setStaged({ type: 'NOTE', label: 'Note' })
            if (onFocused) onFocused()
        }
    }, [focusOnMount, staged, onFocused])

    // When something is staged, focus the textarea.
    useEffect(() => {
        if (staged && textareaRef.current) {
            textareaRef.current.focus()
        }
    }, [staged])

    const [createActivity, { loading }] = useMutation(CREATE_COLLECTION_ACTIVITY, {
        update(cache, { data }) {
            if (!data || !data.createCollectionActivity.ok) return
            const r = data.createCollectionActivity
            const newActivity = {
                __typename: 'CollectionActivityNode',
                id: String(r.activityId),
                caseId: String(caseId),
                activityType: r.activityType,
                note: r.note,
                createdById: r.createdById,
                dateAdded: r.dateAdded,
            }
            try {
                const existing = cache.readQuery({
                    query: CASE_ACTIVITIES,
                    variables: { caseId },
                })
                if (existing) {
                    cache.writeQuery({
                        query: CASE_ACTIVITIES,
                        variables: { caseId },
                        data: {
                            allCollectionActivities: [
                                newActivity,
                                ...existing.allCollectionActivities,
                            ],
                        },
                    })
                }
            } catch (e) {
                /* cache miss is fine */
            }
        },
    })

    const stage = (entry) => {
        setStaged(entry)
        setNoteText(entry.defaultNote || '')
    }
    const cancel = () => {
        setStaged(null)
        setNoteText('')
    }
    const send = () => {
        if (!staged || loading) return
        createActivity({
            variables: {
                caseId,
                activityType: staged.type,
                note: noteText.trim() || null,
            },
        })
        setStaged(null)
        setNoteText('')
    }

    if (staged) {
        return (
            <div className="cd-composer cd-composer-staged">
                <div className="cd-composer-staged-head">
                    <span className="cd-staged-pill">
                        <i className="fa fa-circle" />
                        {staged.label}
                    </span>
                    <button
                        type="button"
                        className="cd-staged-cancel"
                        onClick={cancel}
                        aria-label="Cancel"
                        title="Cancel"
                    >
                        ×
                    </button>
                </div>
                <textarea
                    ref={textareaRef}
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Add a note (optional)…"
                    rows={2}
                    className="cd-composer-textarea"
                    onKeyDown={(e) => {
                        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') send()
                        if (e.key === 'Escape') cancel()
                    }}
                />
                <div className="cd-composer-actions">
                    <button
                        type="button"
                        className="cd-btn outline"
                        onClick={cancel}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="cd-btn success"
                        onClick={send}
                        disabled={loading}
                    >
                        {loading ? 'Sending…' : 'Send'}
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className="cd-composer">
            <div className="cd-composer-hotrow">
                {HOT_BUTTONS.map((b, i) => (
                    <button
                        key={i}
                        type="button"
                        className="cd-hotbutton"
                        onClick={() => stage(b)}
                        title={`Log "${b.label}"`}
                    >
                        <i className={`fa ${b.icon}`} />
                        {b.label}
                    </button>
                ))}
            </div>
            <select
                className="cd-composer-typedropdown"
                value=""
                onChange={(e) => {
                    if (!e.target.value) return
                    stage({ type: e.target.value, label: prettyType(e.target.value) })
                }}
                aria-label="Other activity type"
            >
                <option value="">Other activity type…</option>
                {DROPDOWN_TYPES.map((t) => (
                    <option key={t} value={t}>
                        {prettyType(t)}
                    </option>
                ))}
            </select>
        </div>
    )
}
